import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { Megaphone } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { toast } from "@/hooks/useToast"
import { CallCaseSummaryFeedbackDialog } from "@/modules/patients/detail/progress-note/components/call-case-summary-feedback/call-case-summary-feedback-dialog"
import { useCreateCallCaseSummaryFeedbackMutation } from "@/modules/patients/detail/progress-note/services/useCreateCallCaseSummaryFeedbackMutation"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"

type Props = {
  appointmentId: string
}

const CallCaseSummaryFeedbackButton = ({ appointmentId }: Props) => {
  const { t } = useTranslation()
  const country = useCountryParam()
  const [open, setOpen] = useState(false)
  const [submit, isSubmitting] = useCreateCallCaseSummaryFeedbackMutation(country)

  const handleSubmit = useCallback(
    (feedbackText: string) => {
      submit(
        { appointmentId, feedbackText },
        () => {
          toast({
            description: t("episodeOfCare.progressNote.anamnesis.callCaseSummaryFeedback.successToast"),
          })
          setOpen(false)
        },
        () => {
          toast({
            description: t("chat.ai.errors.UNKNOWN_ERROR"),
          })
        },
      )
    },
    [appointmentId, setOpen, submit, t],
  )

  return (
    <>
      <TooltipCustom>
        <TooltipTrigger asChild>
          <Button
            variant="circle"
            onClick={() => setOpen(true)}
            className="text-on-plum-surface hover:bg-plum-surface-subdued ml-1 h-8 w-8 p-0"
            data-testid="call-case-summary-feedback-button">
            <Megaphone size={20} />
            <span className="sr-only">
              {t("episodeOfCare.progressNote.anamnesis.callCaseSummaryFeedback.iconTooltip")}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="bottom" align="start">
            <span className="p-1">{t("episodeOfCare.progressNote.anamnesis.callCaseSummaryFeedback.iconTooltip")}</span>
          </TooltipContent>
        </TooltipPortal>
      </TooltipCustom>
      <CallCaseSummaryFeedbackDialog
        open={open}
        onOpenChange={setOpen}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </>
  )
}

export { CallCaseSummaryFeedbackButton }
